import type {
  ExpenseBootstrapDTO,
  ExpenseBootstrapRecentActivityDataDTO,
} from "../models/expenseBootstrapDto";
import type { BootstrapSectionDTO } from "../models/clientOverviewBootstrapDto";
import type { BootstrapSectionEntity } from "../../domain/entities/ClientOverviewBootstrapEntity";
import type { ExpenseBootstrapEntity } from "../../domain/entities/ExpenseBootstrapEntity";
import type { RecentExpenseActivityEntity } from "../../domain/entities/RecentExpenseActivityEntity";
import type { RecentExpenseActivityResponseDTO } from "../api/clientOverviewApi";
import { lastMonthExpenseDtoToEntity } from "./lastMonthExpenseMapper";
import { expenseThisMonthDtoToEntity } from "./expenseThisMonthMapper";
import { keyMetricsExpenseDtoToEntity } from "./keyMetricsExpenseMapper";
import { twelveMonthExpenseTrendDtoToEntity } from "./twelveMonthExpenseTrendMapper";
import { expenseBreakdownDtoToEntity } from "./expenseBreakdownMapper";
import { recentExpenseActivityDtoToEntity } from "./recentExpenseActivityMapper";

function mapBootstrapSection<TDto, TEntity>(
  section: { status?: "ok" | "error"; data?: TDto | null; error?: string | null } | undefined,
  mapper: (dto: TDto) => TEntity,
): BootstrapSectionEntity<TEntity> {
  if (!section || section.status !== "ok" || section.data == null) {
    return {
      status: "error",
      data: null,
      error: section?.error ?? "Section unavailable",
    };
  }

  return {
    status: "ok",
    data: mapper(section.data),
    error: null,
  };
}

function mapRecentActivitySection(
  section: BootstrapSectionDTO<ExpenseBootstrapRecentActivityDataDTO> | undefined,
): BootstrapSectionEntity<RecentExpenseActivityEntity> {
  if (!section || section.status !== "ok" || section.data == null) {
    return {
      status: "error",
      data: null,
      error: section?.error ?? "Section unavailable",
    };
  }

  return {
    status: "ok",
    data: recentExpenseActivityDtoToEntity(
      section.data as unknown as RecentExpenseActivityResponseDTO,
    ),
    error: null,
  };
}

export function expenseBootstrapDtoToEntity(dto: ExpenseBootstrapDTO): ExpenseBootstrapEntity {
  const sections = dto.sections ?? ({} as ExpenseBootstrapDTO["sections"]);

  return {
    sections: {
      lastMonth: mapBootstrapSection(sections.lastMonth, lastMonthExpenseDtoToEntity),
      expenseThisMonth: mapBootstrapSection(sections.expenseThisMonth, expenseThisMonthDtoToEntity),
      keyMetrics: mapBootstrapSection(sections.keyMetrics, keyMetricsExpenseDtoToEntity),
      twelveMonthTrend: mapBootstrapSection(
        sections.twelveMonthTrend,
        twelveMonthExpenseTrendDtoToEntity,
      ),
      breakdown: mapBootstrapSection(sections.breakdown, expenseBreakdownDtoToEntity),
      recentActivity: mapRecentActivitySection(sections.recentActivity),
    },
    meta: {
      partial: dto.meta?.partial ?? false,
    },
  };
}
